import { useState, useCallback } from "react";
import { Modal, FormLayout } from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { DatePickerField } from "@shared/ui";
import { AgendaDay, AgendaSwimlane } from "@features/events/types";

interface AddEditDayModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (day: AgendaDay) => void;
  day?: AgendaDay;
  isEditMode?: boolean;
}

export default function AddEditDayModal({
  isOpen,
  onClose,
  onSave,
  day,
  isEditMode = false,
}: AddEditDayModalProps) {
  const { t } = useTranslation();

  // Form state
  const [selectedDateOverride, setSelectedDateOverride] = useState<
    string | null
  >(null);
  const selectedDate = selectedDateOverride ?? day?.date ?? "";

  // Validation
  const validateForm = useCallback(() => {
    if (!selectedDate || selectedDate.trim() === "") {
      return false;
    }
    return true;
  }, [selectedDate]);

  // Handle save
  const handleSave = useCallback(() => {
    if (!validateForm()) {
      return;
    }

    if (!selectedDate || selectedDate.trim() === "") return;

    // Convert string date to Date object for formatting
    const dateObj = new Date(selectedDate);

    // Create default swimlane structure
    const defaultSwimlane: AgendaSwimlane = {
      title: {
        bg: dateObj.toLocaleDateString("bg-BG", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        }),
        en: dateObj.toLocaleDateString("en-US", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        }),
      },
      sessions: day?.swimlines[0]?.sessions || [],
    };

    const dayData: AgendaDay = {
      date: selectedDate, // Already in YYYY-MM-DD format
      swimlines: day?.swimlines || [defaultSwimlane],
    };

    // If editing and has existing swimlines, preserve them but update the date
    if (day && day.swimlines.length > 0) {
      dayData.swimlines = [...day.swimlines];
    }

    onSave(dayData);
    setSelectedDateOverride(null);
    onClose();
  }, [validateForm, selectedDate, day, onSave, onClose]);

  const handleCancel = () => {
    setSelectedDateOverride(null);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleCancel}
      title={
        isEditMode
          ? t("eventDetails.agenda.day.editTitle")
          : t("eventDetails.agenda.day.addTitle")
      }
      primaryAction={{
        content: isEditMode
          ? t("common.update")
          : t("eventDetails.agenda.day.addButton"),
        onAction: handleSave,
      }}
      secondaryActions={[
        {
          content: t("common.cancel"),
          onAction: handleCancel,
        },
      ]}
      onTransitionEnd={() => {
        document.body.setAttribute("data-lock-scrolling-hidden", "true");
      }}
    >
      <Modal.Section>
        <FormLayout>
          {/* Date Selection */}
          <DatePickerField
            label={t("eventDetails.agenda.day.date")}
            value={selectedDate}
            onChange={setSelectedDateOverride}
            isRequired
          />
        </FormLayout>
      </Modal.Section>
    </Modal>
  );
}
